<template>
  <div class="px-3">
    <div class="d-flex align-items-center">
      <h3 class="title mb-1 py-3">Hours and payments report - Overview</h3>
      <div class="ml-3 table-control d-flex align-items-center">
        <b-form-checkbox
          class="switch"
          size="sm"
          switch
          v-model="includeLoggedLastMonth"
        >Include logged hours from last month</b-form-checkbox>
      </div>
      <div class="ml-3 table-control d-flex align-items-center">
        <b-form-checkbox
          class="switch"
          size="sm"
          switch
          v-model="includePaidLastMonth"
        >Include paid hours from last month</b-form-checkbox>
      </div>
    </div>

    <div :class="{'bg-white': !isDataLoading}" class="table-container">
      <div class="d-flex justify-content-between align-items-center bg-white">
        <div class="d-flex align-items-center mb-4" v-if="!isDataLoading">
          <ProjectsStateListComponent
            :projects-list="getProjectsForState()"
            :table-name="uiTableName"
            @changeActive="handleHideProject($event)"
            class="mr-4"
          />
          <div class="d-flex align-items-center table-text">
            <b-form-checkbox
              class="switch"
              size="sm"
              switch
              v-model="showOnlyDebt"
            >Show only projects with debt</b-form-checkbox>
          </div>
        </div>
      </div>

      <div ref="tableContain">
        <b-table-simple
          :fixed="false"
          class="table__theme-default slideout__table position-relative"
          hover
          id="my-table"
          responsive
          stripped
          v-if="!isDataLoading && reportData.length"
        >
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>

          <b-thead head-variant="light">
            <b-tr :class="{'sticky-head': getCoordinates > 0}" :style="{'top': getCoordinates + 'px'}" class="thead-row row-top">
              <b-th :style="{'min-width': thsWidth[`th_project`] + 'px'}" class="text-uppercase">Project and client</b-th>
              <b-th :style="{'min-width': thsWidth[`th_logged`] + 'px'}" class="text-uppercase">Logged hours</b-th>
              <b-th :style="{'min-width': thsWidth[`th_paid`] + 'px'}" class="text-uppercase">Paid hours</b-th>
              <b-th :style="{'min-width': thsWidth[`th_summary`] + 'px'}" class="text-uppercase">Summary</b-th>
              <b-th :style="{'min-width': thsWidth[`th_summary`] + 'px'}" class="text-uppercase">Comment</b-th>
            </b-tr>
            <b-tr>
              <b-th class="text-uppercase" sticky-column><div :ref="`th_project`">Project and client</div></b-th>
              <b-th class="text-uppercase" sticky-column><div :ref="`th_logged`">Logged hours</div></b-th>
              <b-th class="text-uppercase" sticky-column><div :ref="`th_paid`">Paid hours</div></b-th>
              <b-th class="text-uppercase" sticky-column><div :ref="`th_summary`">Summary</div></b-th>
              <b-th class="text-uppercase" sticky-column><div :ref="`th_summary`">Comment</div></b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr :key="i" class="table-row" v-for="(overviewItem, i) in reportData" v-if="projectState[overviewItem.project.id]">
              <b-td sticky-column>
                <div class="d-flex text-left">
                  <b-button
                    :title="'Hours and payments report'"
                    :variant="'secondary'"
                    @click="getSlideOut(overviewItem.project)"
                    class="button hp-btn d-inline-block mr-2"
                    size="sm"
                    v-b-tooltip.hover="{ variant: 'base' }"
                  >
                    HP
                  </b-button>
                  <TableProjectItem
                    :link="overviewItem.project.key"
                    :number="i"
                    :project-clients="overviewItem.project.clientNames"
                    :project-name="overviewItem.project.name"
                    :project-subtitle="overviewItem.project.subtitle"
                    view="table-row"
                  />
                </div>
              </b-td>
              <b-td>
                <div>{{ overviewItem.loggedHours | round(2) }}</div>
              </b-td>
              <b-td>
                <div>{{ overviewItem.paidHours | round(2) }}</div>
              </b-td>
              <b-td :variant="overviewItem.summary < 0 ? 'danger' : 'success'">
                <div>{{ overviewItem.summary | round(2) }}</div>
              </b-td>
              <b-td>
                <div class="user-comment__list p-0">
                  <TextEditor
                    :entity-id="overviewItem.project.id"
                    :entity-type="commentEntityTypes.ReportsHoursAndPayments"
                    :show-text="true"
                    data-already-loaded
                  />
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
      <div class="preloader" v-if="isDataLoading">
        <div class="spinner-border spinner-theme_default text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <div v-if="!isDataLoading && reportData.length === 0">
      </div>
    </div>
    <transition name="show">
      <div class="slide-out__container" v-if="showSlideOut">
        <button
          @click="closeSlideOut()"
          aria-label="Close"
          class="close close-slideout"
          type="button"
        >
            <span class="material-icons">
              close
            </span>
        </button>
        <TableWithDetails :current-project="openProject" />
      </div>
    </transition>
  </div>
</template>

<script>
  import {mapActions, mapGetters} from 'vuex'
  import {cloneDeep} from 'lodash'
  import {permissions} from '@mocks/permissions'
  import {commentEntityTypes} from '@mocks/comment-entity-types'
  import StickyHeaderMixin from '@/mixins/StickyHeaderMixin'
  import ProjectsStateListComponent from '@components/ProjectsStateListComponent'
  import TableProjectItem from '@components/tables/TableProjectItem'
  import TableWithDetails from '@components/tables/TableWithDetails'
  import TextEditor from '@components/TextEditor'

  export default {
  name: 'TableHPReportOverview',
  mixins: [ StickyHeaderMixin ],
  components: {
    ProjectsStateListComponent,
    TableProjectItem,
    TableWithDetails,
    TextEditor
  },
  data () {
    return {
      permissions,
      uiTableName: 'hours_and_payments_report_overview',
      isDataLoading: false,
      includeLoggedLastMonth: false,
      includePaidLastMonth: false,
      showOnlyDebt: true,
      reportData: [],
      showSlideOut: false,
      openProject: null,
      showEditor: false,
      projectState: {},
      allActiveProjects: 0,
      commentEditorOptions: {
        entity_type: commentEntityTypes.ReportsHoursAndPayments,
        data_already_loaded: true
      }
    }
  },
  async mounted () {
    await this.fetchComments({ type: commentEntityTypes.ReportsHoursAndPayments })
    await this.fetchReportsHoursAndPaymentsOverviewReport()
  },
  computed: {
    commentEntityTypes () {
      return commentEntityTypes
    },
    ...mapGetters({
      getTableHiddenColumnKeys: 'frontView/getTableHiddenColumnKeys'
    })
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments',
      addHiddenColumnKey: 'frontView/addHiddenColumnKey',
      removeHiddenColumnKey: 'frontView/removeHiddenColumnKey'
    }),
    getSum (arr, key) {
      let sum = 0
      for (let i in arr) {
        sum += arr[i][key]
      }
      return sum
    },
    getAmount (data) {
      return data.amount + data.amount_not_paid
    },
    getSlideOut (project) {
      this.showSlideOut = true
      this.openProject = {
        id: project.id,
        name: project.name,
        key: project.key
      }
      window.scrollTo(0, 0)
    },
    closeSlideOut () {
      this.showSlideOut = false
      this.openProject = null
    },
    fetchReportsHoursAndPaymentsOverviewReport () {
      return new Promise((resolve, reject) => {
        this.isDataLoading = true
        const requestParams = {
          includeLastMonthLogs: this.includeLoggedLastMonth,
          includeLastMonthPaid: this.includePaidLastMonth,
          showOnlyDebt: this.showOnlyDebt
        }
        return this.$api.finances
          .getReportsHoursAndPaymentsOverview(requestParams)
          .then(response => {
            if (response.status === 200) {
              this.reportData = response.data
            }
            resolve(true)
          })
          .catch(error => {
            const errors = Object.values(error.response.data.errors)
            for (var i = 0; i < errors.length; i++) {
              this.$snotify.error(
                errors[i][0],
                'Error',
                this.$store.state.snotify
              )
            }
            reject(error)
          })
          .finally(() => {
            this.isDataLoading = false
          })
      })
    },
    getProjectsForState () {
      const reportItems = cloneDeep(this.reportData)
      return reportItems.map(item => item.project)
    },
    handleHideProject (disProjects) {
      const projects = this.getProjectsForState()
      for (const i in projects) {
        if (disProjects.some(id => projects[i].id === id)) {
          this.projectState[projects[i].id] = false
        } else {
          this.projectState[projects[i].id] = true
        }
      }
      this.allActiveProjects = Object.values(this.projectState).filter(item => !!item).length
      this.$forceUpdate()
    }
  },
  watch: {
    'includeLoggedLastMonth': function () {
      this.fetchReportsHoursAndPaymentsOverviewReport()
    },
    'includePaidLastMonth': function () {
      this.fetchReportsHoursAndPaymentsOverviewReport()
    },
    'showOnlyDebt': function () {
      this.fetchReportsHoursAndPaymentsOverviewReport()
    }
  }
}
</script>

<style lang="scss" scoped>
.table-container {
  padding: 10px;
  border-radius: 10px;

  .preloader {
    background-color: transparent;
  }

  .table-row {
    height: 30px;
    td {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }

  .table-text {
    font-size: 12px;
    font-weight: 500;
    color: #7489A9;
  }
}
.slideout__table {
  &.table__theme-default {
    .table {
      background-color: #fff;
      thead {
        border-bottom: 1px solid #dee2e6;
      }
      tr {
        th {
          text-transform: none;
          background-color: #fff;
        }
      }
    }
  }
  .group-border {
    border-left: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
  }
  .tfoot {
    font-weight: bold;
    border-top: 1px solid #E4E7EE;
  }
}
.member-name {
  max-width: 200px;
  min-width: 200px;

  ::v-deep.user-name {
    max-width: 140px;
  }
  ::v-deep.user-position {
    max-width: 120px;
  }
}
.month-group {
  min-width: 70px;
}
.commission {
  font-size: 16px;
  cursor: pointer;
  color: #3D5170;
  .text-on {
    color: #3FA52F;
  }
  .text-off {
    color: #DC3545;
  }
}
.thead-row {
  display: none;
  &.sticky-head {
    min-height: 70px;
    padding: 0;
    display: table-row;
    align-items: center;
    background-color: #fff;
    position: absolute;
    left: 0;
    right: 0;
    z-index: 9;

    .sticky-head__second-line {
      span {
        padding: 6px 5px 10px;

        &:first-child {
          padding: 6px 10px 10px 0;
        }
        &:last-child {
          padding: 6px 0px 10px 10px;
        }
      }
    }
  }
}
.invoice-badge {
  font-size: 14px;
  padding: 5px 12px;
  background-color: #DEEBFF;
}
.not-paid{
  background-color: #ede742;
}
.invoice-more {
  height: 100%;
  color: #007bff;
  cursor: pointer;
}
.invoice-td {
  display: flex;
  align-items: center;
  .b-toast {
    position: absolute;
  }
}
.variable-td {
  min-width: 100px;
  .val {
    font-weight: bold;
    text-decoration: underline;
  }

  input.form-control {
    width: 40px;
    height: 25px;
    padding: 4px;
    min-height: 25px;
    text-align: center;
  }
}
.material-icons {
  &.done {
    color: #3FA52F;
  }
  &.cancel {
    color: #DC3545;
  }
}
.multiselect-projects {
  width: 200px;
}
</style>
